import { API_URL } from './cognito';

// Strip trailing slash from base + ensure path starts with one, so we never
// produce a URL like `.../prod//api/users/me`. API Gateway can't match that
// against `/{proxy+}` and returns its default 404 (with no CORS header), which
// shows up in the browser as a misleading CORS error.
const BASE = API_URL.replace(/\/+$/, '');

// On network failure (offline, CORS preflight failure, DNS, etc.) fetch throws.
// Letting that propagate forces every caller to wrap in try/catch and most of
// them don't. Instead we return a synthetic Response with `status: 0` so the
// existing `res.ok` / `res.status` checks keep working, and a `networkError`
// flag for code that wants to react specifically to "couldn't reach server"
// (e.g. show "you're offline" toast).
export interface ApiResponse extends Response {
  networkError?: boolean;
}

export async function api(
  path: string,
  opts: { method?: string; token?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<ApiResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const p = path.startsWith('/') ? path : `/${path}`;
  try {
    return await fetch(`${BASE}${p}`, {
      method: opts.method || 'GET',
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    // AbortError is intentional (caller cancelled) — surface it so the caller
    // can ignore stale data. Everything else gets the synthetic offline shape.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    const synthetic = new Response(null, {
      status: 0,
      statusText: err instanceof Error ? err.message : 'Network error',
    }) as ApiResponse;
    synthetic.networkError = true;
    return synthetic;
  }
}
